import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import clsx from 'clsx';

import Sheet from '@/features/chat/components/Sheet';
import { Segmented } from '@/components/ui';
import type { PointDraft } from '@/lib/drafts';
import { formatKop, formatQty, parseRub } from '@/lib/format';
import { PAY_METHOD_LABEL, type PayMethod, type PayMode } from '@/services/courier';

import type { VisitMoney } from './visit-math';

const METHODS: PayMethod[] = ['CASH', 'CARD', 'TRANSFER'];
/** Копейки → строка для поля «Получено»: без копеек, если их нет. */
const RUB_INPUT = (kop: number) => (kop % 100 === 0 ? String(kop / 100) : (kop / 100).toFixed(2).replace('.', ','));

/**
 * Расчёт на точке: отгрузка + долг = к оплате; курьер вводит, сколько получил.
 * Возвраты и обмены — товаром, сумму не меняют, но видны в отчёте визита.
 * Режим магазина закрепил руководитель: «только наличка» — вся отгрузка
 * наличными сразу, «только в долг» — денег не берём.
 */
export function PaySheet({
  money,
  swappedUnits,
  debt,
  payMode = 'FREE',
  payMethods,
  debtLimit,
  otherDebt = 0,
  draft,
  busy,
  busyLabel,
  error,
  onDraft,
  onClose,
  onSubmit,
}: {
  money: VisitMoney;
  /** Сколько забрано у точки (возвраты и обмены), в единицах товаров. */
  swappedUnits: number;
  debt: number;
  payMode?: PayMode;
  /** Способы этого магазина. Пусто — три по умолчанию. */
  payMethods?: PayMethod[];
  debtLimit?: number | null;
  otherDebt?: number;
  draft: PointDraft;
  busy: boolean;
  busyLabel?: string | null;
  /** Почему не отметили: геопозиция, фото, ответ сервера. */
  error?: string | null;
  onDraft: (patch: Partial<PointDraft>) => void;
  onClose: () => void;
  onSubmit: (paidAmount: number) => void;
}) {
  const methods = payMethods?.length ? payMethods : METHODS;
  const due = money.delivered;
  const total = debt + due;
  const toPay = Math.max(0, total);
  const debtOnly = payMode === 'DEBT';
  const cashOnly = payMode === 'CASH';
  const [inputError, setError] = useState<string | null>(null);
  const paid = debtOnly ? 0 : draft.paid.trim() === '' ? null : parseRub(draft.paid);
  const after = paid == null ? null : total - paid;

  // «Только наличка»: сразу подставляем отгрузку — меньше её получить нельзя.
  useEffect(() => {
    if (cashOnly && draft.paid.trim() === '' && due > 0) onDraft({ paid: RUB_INPUT(due) });
    // Только при открытии листа.
  }, []);

  const quick: Array<{ label: string; kop: number }> = [];
  if (cashOnly) {
    if (debt > 0) quick.push({ label: `С долгом ${formatKop(toPay)}`, kop: toPay });
    quick.push({ label: `За отгрузку ${formatKop(due)}`, kop: due });
  } else {
    quick.push({ label: `Всё ${formatKop(toPay)}`, kop: toPay });
    if (debt > 0 && due > 0) quick.push({ label: `Только сегодня ${formatKop(due)}`, kop: due });
    quick.push({ label: 'Ничего', kop: 0 });
  }

  const overLimit = (next: number) => debtLimit != null && next > debt && otherDebt + next > debtLimit;

  const submit = () => {
    if (debtOnly) {
      if (overLimit(total)) {
        setError(`Лимит долга магазина ${formatKop(debtLimit!)} исчерпан — в долг больше не отгрузить. Позвоните руководителю`);
        return;
      }
      onSubmit(0);
      return;
    }
    if (paid == null) {
      setError(draft.paid.trim() === '' ? 'Сколько получили? Если ничего — нажмите «Ничего»' : 'Не похоже на сумму');
      return;
    }
    if (cashOnly && paid < due) {
      setError(`Магазин работает только за наличные сразу — получите минимум ${formatKop(due)}`);
      return;
    }
    if (!cashOnly && debtLimit != null && otherDebt + (total - paid) > debtLimit) {
      const must = otherDebt + total - debtLimit;
      setError(`Нужно получить минимум ${formatKop(Math.max(0, must))} — лимит долга магазина ${formatKop(debtLimit)}`);
      return;
    }
    onSubmit(paid);
  };

  const swapped = money.taken > 0 || money.given > 0;

  return (
    <Sheet title="Оплата" onClose={onClose}>
      {payMode !== 'FREE' && (
        <p
          className={clsx(
            'mb-4 rounded-xl px-3 py-2.5 text-[14px]',
            debtOnly ? 'bg-amber-50 text-amber-900' : 'bg-emerald-50 text-emerald-900',
          )}
        >
          {debtOnly
            ? 'Магазин работает только в долг — денег не берём. Долг гасит руководитель.'
            : 'Магазин работает только за наличные — получите всю отгрузку сразу.'}
        </p>
      )}

      <h3 className="mb-1.5 text-[13px] font-medium text-ink/50">Отчёт по точке</h3>
      <dl className="space-y-1.5 text-[15px]">
        <Line label="Отгружено" value={formatKop(due)} />
        {swapped && (
          <>
            <Line
              label={`Забрали у точки${swappedUnits > 0 ? ` · ${formatQty(swappedUnits)} ед.` : ''}`}
              value={`товар на ${formatKop(money.taken)}`}
              muted
            />
            <Line label="Выдали взамен" value={`товар на ${formatKop(money.given)}`} muted />
          </>
        )}
        {debt !== 0 && <Line label={debt > 0 ? 'Долг точки' : 'Переплата точки'} value={`${debt > 0 ? '+' : '−'}${formatKop(Math.abs(debt))}`} />}
        <div className="flex items-baseline justify-between border-t border-line pt-2">
          <dt className="font-medium text-ink">{debtOnly ? 'Уйдёт в долг' : 'К оплате'}</dt>
          <dd className="text-xl font-semibold tabular-nums text-ink">{formatKop(debtOnly ? due : toPay)}</dd>
        </div>
      </dl>
      {swapped && (
        <p className="mt-2 text-[13px] text-ink/50">
          Возврат и обмен — товаром: забранное спишется, выданное взамен уйдёт из машины. Сумму к оплате они не меняют.
        </p>
      )}

      {!debtOnly && (
        <>
          <label className="mt-5 block">
            <span className="label">Получено, ₽</span>
            <input
              className="input text-lg font-semibold tabular-nums"
              inputMode="decimal"
              placeholder="0"
              value={draft.paid}
              onChange={(e) => {
                setError(null);
                onDraft({ paid: e.target.value.replace(/[^\d.,\s]/g, '') });
              }}
            />
          </label>
          <div className="mt-2 flex flex-wrap gap-2">
            {quick.map((q) => (
              <button
                key={q.label}
                type="button"
                className={clsx('btn-sm btn border', paid === q.kop ? 'border-orange bg-orange-50 text-orange-700' : 'border-line bg-white text-ink/70')}
                onClick={() => {
                  setError(null);
                  onDraft({ paid: RUB_INPUT(q.kop) });
                }}
              >
                {q.label}
              </button>
            ))}
          </div>

          <div className="mt-4">
            <span className="label">Как заплатили</span>
            {cashOnly ? (
              <p className="rounded-xl border border-line bg-ink/[0.02] px-3 py-2.5 text-[15px] text-ink">{PAY_METHOD_LABEL.CASH}</p>
            ) : (
              <Segmented
                value={draft.method}
                onChange={(method) => onDraft({ method })}
                options={methods.map((m) => ({ value: m, label: PAY_METHOD_LABEL[m] }))}
                className="flex w-full"
              />
            )}
          </div>
        </>
      )}

      {after != null && (
        <p
          className={clsx(
            'mt-4 rounded-xl px-3 py-2.5 text-[14px]',
            after > 0 ? 'bg-red-50 text-red-800' : 'bg-emerald-50 text-emerald-800',
          )}
        >
          {after > 0
            ? `${debtOnly ? 'Долг магазина станет' : 'Останется долг'} ${formatKop(after)}`
            : after < 0
              ? `Переплата ${formatKop(-after)} — зачтётся в следующий визит`
              : 'Долга не останется'}
        </p>
      )}
      {debtOnly && overLimit(total) && (
        <p className="mt-2 text-[13px] text-red-700">
          Лимит долга магазина {formatKop(debtLimit!)} — с этой отгрузкой он будет превышен.
        </p>
      )}

      <label className="mt-4 block">
        <span className="label">Комментарий</span>
        <input className="input" value={draft.comment} onChange={(e) => onDraft({ comment: e.target.value })} maxLength={500} placeholder="Необязательно" />
      </label>

      {(error || inputError) && <p className="mt-3 rounded-xl bg-red-50 px-3 py-2.5 text-[14px] text-red-800">{inputError ?? error}</p>}
      <button type="button" className="btn-primary mt-5 w-full" onClick={submit} disabled={busy}>
        {busy && <Loader2 size={18} className="animate-spin" />}
        {busy && busyLabel ? busyLabel : debtOnly ? 'Отметить — в долг' : 'Отметить выполненной'}
      </button>
    </Sheet>
  );
}

function Line({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-ink/60">{label}</dt>
      <dd className={clsx('tabular-nums', muted ? 'text-ink/60' : 'text-ink')}>{value}</dd>
    </div>
  );
}
